import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { PropertyCosts, PropertyCostsInput } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { centsToEuros, eurosToCents } from '@/lib/money';

const costsFormSchema = z.object({
  purchasePrice: z.number().positive('Requis'),
  notaryFees: z.number().nonnegative(),
  works: z.number().nonnegative(),
  plan: z.number().nonnegative(),
  furniture: z.number().nonnegative(),
  agencyFees: z.number().nonnegative(),
  guaranteeFees: z.number().nonnegative(),
});

type CostsFormValues = z.infer<typeof costsFormSchema>;

const FIELDS: { name: keyof CostsFormValues; label: string }[] = [
  { name: 'purchasePrice', label: "Prix d'acquisition" },
  { name: 'notaryFees', label: 'Frais de notaire' },
  { name: 'works', label: 'Travaux' },
  { name: 'plan', label: 'Plan / architecte' },
  { name: 'furniture', label: 'Mobilier' },
  { name: 'agencyFees', label: "Honoraires d'agence" },
  { name: 'guaranteeFees', label: 'Frais de garantie' },
];

function toFormValues(costs: PropertyCosts | null | undefined): CostsFormValues {
  return {
    purchasePrice: centsToEuros(costs?.purchasePriceCents ?? 0),
    notaryFees: centsToEuros(costs?.notaryFeesCents ?? 0),
    works: centsToEuros(costs?.worksCents ?? 0),
    plan: centsToEuros(costs?.planCents ?? 0),
    furniture: centsToEuros(costs?.furnitureCents ?? 0),
    agencyFees: centsToEuros(costs?.agencyFeesCents ?? 0),
    guaranteeFees: centsToEuros(costs?.guaranteeFeesCents ?? 0),
  };
}

function toInput(values: CostsFormValues): PropertyCostsInput {
  return {
    purchasePriceCents: eurosToCents(values.purchasePrice),
    notaryFeesCents: eurosToCents(values.notaryFees),
    worksCents: eurosToCents(values.works),
    planCents: eurosToCents(values.plan),
    furnitureCents: eurosToCents(values.furniture),
    agencyFeesCents: eurosToCents(values.agencyFees),
    guaranteeFeesCents: eurosToCents(values.guaranteeFees),
  };
}

export function PropertyCostsForm({
  costs,
  onSubmit,
  isPending,
}: {
  costs: PropertyCosts | null | undefined;
  onSubmit: (input: PropertyCostsInput) => void;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CostsFormValues>({
    resolver: zodResolver(costsFormSchema),
    // `values` (pas `defaultValues`) : le formulaire se resynchronise si `costs` change après
    // le montage (ex. un refetch en arrière-plan), au lieu de figer les valeurs au premier rendu.
    values: toFormValues(costs),
  });

  return (
    <form onSubmit={handleSubmit((values) => onSubmit(toInput(values)))} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {FIELDS.map(({ name, label }) => (
          <div key={name} className="flex flex-col gap-1.5">
            <Label htmlFor={name}>{label} (€)</Label>
            <Input
              id={name}
              type="number"
              step="0.01"
              min="0"
              {...register(name, { valueAsNumber: true })}
            />
            {errors[name] && <p className="text-sm text-destructive">{errors[name]?.message}</p>}
          </div>
        ))}
      </div>
      <Button type="submit" disabled={isPending} className="self-start">
        {isPending ? 'Enregistrement…' : 'Enregistrer les coûts'}
      </Button>
    </form>
  );
}
